import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, Package } from "lucide-react";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import BundleProductGroup from "@/features/bundles/ui/bundle-product-group";
import BundleGroupedFilterBar, {
  FilterProduct,
} from "@/features/bundles/ui/bundle-grouped-filter-bar";
import ProductVisibilityPanel from "@/features/bundles/ui/product-visibility-panel";
import { ContentLayout } from "@/components/common/content-layout";
import { Button } from "@/components/ui/button";
import { useBundleFilters } from "@/features/bundles/hooks/use-bundle-filters";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";

export default function Bundles() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const search = searchParams.get("search") || "";

  const { data, isLoading, isFetching, error } = useBundlesGrouped({
    q: search.trim() || undefined,
  });

  const groups = useMemo(() => data?.groups ?? [], [data]);

  // Filters resolve URL → localStorage → default, and write to both on change.
  const [filters, setFilters] = useBundleFilters();
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  // Products for the multi-select / visibility panel (grouped products only).
  const products: FilterProduct[] = useMemo(
    () =>
      groups
        .map((g) => g.product)
        .filter((p): p is NonNullable<typeof p> => !!p)
        .map((p) => ({
          id: p.id,
          name: p.name,
          type: p.type,
          status: p.status,
          on_dashboard: p.on_dashboard,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [groups],
  );

  const { types, productIds, productStatus, productTypes } = filters;
  const hasProductFilter =
    productIds.length > 0 ||
    productStatus.length > 0 ||
    productTypes.length > 0;

  // Apply filters: bundle-type on offerings, then product-level on groups.
  const filteredGroups = useMemo(() => {
    return groups
      .map((g) => {
        // Untyped legacy bundles behave as "standard" (the common default).
        const offerings =
          types.length > 0
            ? g.offerings.filter((o) => types.includes(o.type ?? "standard"))
            : g.offerings;
        return { ...g, offerings };
      })
      .filter((g) => {
        if (g.offerings.length === 0) return false;
        const p = g.product;
        // The "No Product" bucket only shows when no product-scoping is active.
        if (!p) return !hasProductFilter;
        if (productIds.length && !productIds.includes(p.id)) return false;
        if (productStatus.length && !productStatus.includes(p.status))
          return false;
        if (productTypes.length && !productTypes.includes(p.type)) return false;
        return true;
      });
  }, [groups, types, productIds, productStatus, productTypes, hasProductFilter]);

  // Open the bundle's detail page (replaces the old edit drawer).
  const openBundle = (bundle: GroupedBundleLeaf | null) => {
    if (bundle?.id) navigate(bundle.id);
  };

  return (
    <ContentLayout
      header={
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <BundleGroupedFilterBar
            products={products}
            filters={filters}
            onChange={setFilters}
            onOpenVisibility={() => setVisibilityOpen(true)}
          />
          <Button
            variant="outline"
            onClick={() => navigate("../products")}
            title="View all products"
          >
            <Package className="h-4 w-4 mr-1" /> Products
          </Button>
        </div>
      }
      data={filteredGroups}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load bundles"
      emptyState={{
        message:
          groups.length > 0
            ? "No bundles match the current filters"
            : "No bundles found",
        icon: <CalendarDays className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <div className="space-y-4">
        {filteredGroups.map((group, i) => (
          <BundleProductGroup
            key={group.product?.id ?? `no-product-${i}`}
            group={group}
            onBundleClick={openBundle}
          />
        ))}
      </div>

      <ProductVisibilityPanel
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
        products={products}
        selectedIds={productIds}
        onToggle={(id) =>
          setFilters({
            ...filters,
            productIds: productIds.includes(id)
              ? productIds.filter((p) => p !== id)
              : [...productIds, id],
          })
        }
        onClear={() => setFilters({ ...filters, productIds: [] })}
      />
    </ContentLayout>
  );
}
